/**
 * Read a fetch Response defensively against PostHog (and similar
 * session-recording / RUM tools) that monkey-patch `window.fetch` and
 * consume the response body before our code can read it. Without this,
 * production calls fail with:
 *
 *   "Failed to execute 'json' on 'Response': body stream already read"
 *
 * even though the server returned a perfectly valid payload — and any
 * code in the catch block (toasts, error UI) runs as if the backend had
 * failed when in reality the call succeeded server-side.
 *
 * Strategy: clone the response IMMEDIATELY so we have an independent
 * reader, read as text, then JSON-parse ourselves. Falls back to reading
 * the original Response if cloning fails for any reason.
 *
 * Always returns `{ data, text }` so callers can branch on `res.ok`
 * without worrying about the body parse throwing.
 */
export async function readResponse(res) {
  let text = '';
  try { text = await res.clone().text(); }
  catch {
    try { text = await res.text(); }
    catch { text = ''; }
  }
  let data = {};
  if (text) {
    try { data = JSON.parse(text); }
    catch { /* non-JSON body (HTML error page, etc.) — keep `data` empty */ }
  }
  return { data, text };
}

/**
 * Convenience wrapper for `.then(r => r.json())` chains. Returns just
 * the parsed body so existing `.then(d => …)` callbacks keep their
 * shape. Always resolves — never rejects on parse failure.
 */
export async function readResponseJSON(res) {
  const { data } = await readResponse(res);
  return data;
}

export default readResponse;
